// ✅ 모바일 전용 ReportEditor (PlanEditor) - 웹사이트 코드에 영향 없음
import React, { useEffect, useMemo, useState, useRef } from "react";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import MobileAttachmentBox from "../../common/papers/MobileAttachmentBox";

export default function MobileReportEditor({
  initial = {},
  onChange,
  readOnly = false,
  currentUser = {},
}) {
  const editorRef = useRef(null);
  const attachmentRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  const safeUser =
    typeof currentUser === "string"
      ? (() => {
          try {
            return JSON.parse(currentUser);
          } catch {
            return {};
          }
        })()
      : currentUser || {};

  const [form, setForm] = useState({
    doc_date: initial.doc_date || today,
    status: initial.status || "none",
    title: initial.title || "",
    author: initial.author || safeUser.name || "",
    version: initial.version || "1.0",
    note: initial.note || "",
  });

  const onForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const autoTitle = useMemo(() => {
    const a = form.author?.trim();
    const v = form.version?.trim();
    const date = form.doc_date?.replace(/-/g, "");

    if (a && v) return `[PLAN] 안전관리계획서 v${v} - ${a} (${date})`;
    if (v) return `[PLAN] 안전관리계획서 v${v} (${date})`;
    return `[PLAN] 안전관리계획서 (${date})`;
  }, [form.author, form.version, form.doc_date]);

  useEffect(() => {
    if (!onChange) return;

    const meta = {
      doc_date: form.doc_date || today,
      author: form.author,
      version: form.version,
      note: form.note,
    };

    const summary = {
      title: form.title?.trim() || autoTitle,
      requestDept: "-",
      reason: form.note,
      details: [
        { label: "작성자", value: form.author },
        { label: "버전", value: form.version },
        { label: "작성일", value: form.doc_date },
      ],
      attachments: attachmentRef.current?.files || [],
    };

    onChange({
      meta,
      summary,
    });
  }, [form, autoTitle, onChange, today]);

  return (
    <div className="space-y-4 min-w-0 overflow-x-hidden">
      {/* 제목 */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-gray-700 block">
          제목
        </label>
        <input
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={autoTitle}
          value={form.title}
          onChange={(e) => onForm("title", e.target.value)}
          disabled={readOnly}
        />
      </div>

      {/* 버전 + 상태 */}
      <div className="grid grid-cols-1 gap-3">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            버전
          </label>
          <input
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={form.version}
            onChange={(e) => onForm("version", e.target.value)}
            disabled={readOnly}
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            상태
          </label>
          <div className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-700">
            {form.status === "draft"
              ? "임시저장"
              : form.status === "approved"
              ? "승인"
              : "진행중"}
          </div>
        </div>
      </div>

      {/* 작성일 + 작성자 */}
      <div className="grid grid-cols-1 gap-3">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            작성일
          </label>
          <input
            type="date"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={form.doc_date}
            onChange={(e) => onForm("doc_date", e.target.value)}
            disabled={readOnly}
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            작성자
          </label>
          <input
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={form.author}
            onChange={(e) => onForm("author", e.target.value)}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* 비고 */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-gray-700 block">
          비고
        </label>
        <div className="min-w-0 w-full">
          <TinyMCEeditor
            initialValue={form.note}
            editorRef={editorRef}
            onChange={(v) => onForm("note", v)}
          />
        </div>
      </div>

      {/* 첨부파일 */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-gray-700 block">
          첨부파일
        </label>
        <MobileAttachmentBox ref={attachmentRef} />
      </div>
    </div>
  );
}

